import { useRef } from 'react';
import { toast } from 'react-toastify';
import {
  DrawerTooltipContainer,
  DrawerTooltipContainerRef,
} from 'src/v2/commonComponents/DrawerTooltipContainer';
import { CodeLanguageToggle } from '../CodeLanguageToggle';
import { Button } from 'src/v2/commonComponents/Button';
import styles from './CodeLanguageControl.module.scss';
import {
  useCodeEditorActions,
  useEditorState,
  useRunEditorStream,
} from 'src/hooks';
import {
  canConvertLanguages,
  getCodeEditorChangeLanguagePrompt,
  getHumanMessage,
} from 'src/utils';
import { PROGRAMMING_LANGUAGES } from 'src/constants';
import { ProgrammingLanguage } from 'src/types';

const tooltipAnchorId = 'code-language-button-tooltip-anchor';

export const CodeLanguageControl = () => {
  const drawerRef = useRef<DrawerTooltipContainerRef>(null);

  const { codeEditorData } = useEditorState();
  const { setCodeEditorData } = useCodeEditorActions();
  const { onRunEditorStream } = useRunEditorStream();

  const handleClick = async (language: ProgrammingLanguage) => {
    drawerRef.current?.onVisibilityToggle();

    if (!codeEditorData?.content || !codeEditorData?.language) {
      setCodeEditorData({
        language,
      });

      return;
    }

    if (
      codeEditorData?.content &&
      !canConvertLanguages(codeEditorData?.language, language)
    ) {
      toast.error('Code translation between these languages is not possible');
      return;
    }

    setCodeEditorData({
      language,
    });

    const content = getCodeEditorChangeLanguagePrompt(
      codeEditorData?.language,
      language,
    );

    const userMessage = getHumanMessage(content);

    const input = {
      userMessage,
    };
    await onRunEditorStream(input);
  };

  return (
    <DrawerTooltipContainer
      ref={drawerRef}
      id={tooltipAnchorId}
      place="bottom"
      offset={8}
      classNameTooltip={styles.tooltip}
      ToggleButton={CodeLanguageToggle}
    >
      <div className={styles.container}>
        {PROGRAMMING_LANGUAGES.map(({ label, language }) => (
          <Button
            key={language}
            onClick={() => handleClick(language)}
            className={styles.item}
            color="transparent"
          >
            <div className={styles.labelWrapper}>
              <span>{label}</span>
            </div>
          </Button>
        ))}
      </div>
    </DrawerTooltipContainer>
  );
};
